import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Home, 
  Building, 
  Factory, 
  Settings, 
  Wrench, 
  Shield,
  Zap,
  Globe,
  CheckCircle,
  ArrowRight
} from 'lucide-react';
import { openWhatsAppQuote } from '../utils/whatsapp';

const Products: React.FC = () => {
  const products = [
    // 1. Balcony Grills
    {
      icon: <Shield className="h-16 w-16 text-blue-500" />,
      title: "Balcony Grills",
      description: "Transparent safety grills specifically designed for balcony protection without compromising your view",
      features: [
        "Balcony-specific transparent design",
        "Weather-resistant installation",
        "Child safety protection",
        "Unobstructed panoramic views",
        "Easy maintenance and cleaning"
      ],
      material: "High-grade transparent materials",
      warranty: "5 years"
    },
    // 2. Window Grills
    {
      icon: <Shield className="h-16 w-16 text-blue-500" />,
      title: "Window Grills",
      description: "Transparent safety grills for window protection and security without compromising natural light",
      features: [
        "Window-specific transparent design",
        "Security enhancement without blocking light",
        "Child safety protection",
        "Unobstructed window views",
        "Easy maintenance and cleaning"
      ],
      material: "High-grade transparent materials",
      warranty: "5 years"
    },
    // 3. Apartment Grills
    {
      icon: <Shield className="h-16 w-16 text-blue-500" />,
      title: "Apartment Grills",
      description: "Comprehensive transparent safety solutions for apartment buildings and communities",
      features: [
        "Apartment-specific transparent design",
        "Bulk installation capabilities",
        "Community safety solutions",
        "Unobstructed building views",
        "Scalable installation for multiple units"
      ],
      material: "High-grade transparent materials",
      warranty: "5 years"
    },
    // 4. Anti Bird Net (Pigeon Nets)
    {
      icon: <Shield className="h-16 w-16 text-blue-500" />,
      title: "Anti Bird Net",
      description: "Anti-bird nets to protect buildings and structures from pigeons and other birds",
      features: [
        "Pigeon deterrent nets",
        "Balcony protection",
        "Window safety nets",
        "Building facade protection",
        "Bird control solutions"
      ],
      material: "Heavy-duty nylon mesh",
      warranty: "5 years"
    },
    // 5. Cricket Practice Net (Sports Safety)
    {
      icon: <Building className="h-16 w-16 text-blue-500" />,
      title: "Cricket Practice Net",
      description: "Professional cricket practice nets for training facilities and sports areas",
      features: [
        "Cricket practice nets",
        "Football goal nets",
        "Tennis court nets",
        "Golf range nets",
        "Multi-sport protection"
      ],
      material: "UV-resistant nylon",
      warranty: "3 years"
    },
    // 6. Children Safety Nets
    {
      icon: <Shield className="h-16 w-16 text-blue-500" />,
      title: "Children Safety Nets",
      description: "Protective safety nets for children's safety in homes and playgrounds",
      features: [
        "Balcony child protection",
        "Window safety barriers",
        "Playground safety nets",
        "Staircase protection",
        "Child-proof installations"
      ],
      material: "Premium safety mesh",
      warranty: "5 years"
    },
    // 7. Drying Cloth Hangers
    {
      icon: <Settings className="h-16 w-16 text-blue-500" />,
      title: "Drying Cloth Hangers",
      description: "Convenient retractable cloth hangers for drying clothes",
      features: [
        "Retractable design",
        "Space-saving",
        "Easy operation",
        "Durable construction",
        "Multiple hooks"
      ],
      material: "Stainless steel with nylon cord",
      warranty: "3 years"
    },
    // 8. Shade Net
    {
      icon: <Shield className="h-16 w-16 text-blue-500" />,
      title: "Shade Net",
      description: "Protective shade nets for agricultural and commercial applications",
      features: [
        "Sun protection",
        "Temperature control",
        "Crop protection",
        "UV resistance",
        "Agricultural use"
      ],
      material: "UV-resistant shade mesh",
      warranty: "4 years"
    }
    ,
    // 9. Balcony Safety Nets
    {
      icon: <Shield className="h-16 w-16 text-blue-500" />,
      title: "Balcony Safety Nets",
      description: "Durable balcony safety nets to protect children, pets, and belongings",
      features: [
        "High-strength netting",
        "Child & pet protection",
        "Weather-resistant",
        "Custom sizing",
        "Low maintenance"
      ],
      material: "UV-stabilized net",
      warranty: "5 years"
    }
  ];

  const productRoutes = [
    '/invisible-grill-balconies',
    '/invisible-grill-windows',
    '/invisible-grill-apartments',
    '/pigeon-nets',
    '/sports-safety',
    '/child-safety-net',
    '/pull-dry-cloth-hangers',
    '/shade-net',
    '/balcony-safety-nets'
  ];

  const additionalServices = [
    {
      icon: <Settings className="h-12 w-12 text-blue-500" />,
      title: "Custom Design & Engineering",
      description: "Professional safety net design with 3D modeling and optimization"
    },
    {
      icon: <Wrench className="h-12 w-12 text-green-500" />,
      title: "Installation & Commissioning",
      description: "Expert installation with quality assurance and testing"
    },
    {
      icon: <Shield className="h-12 w-12 text-purple-500" />,
      title: "Maintenance & Support",
      description: "Regular maintenance, monitoring, and 24/7 support"
    }
  ];

  return (
    <div className="pt-16">
      {/* Hero Section */}
      <section className="relative text-white py-20 overflow-hidden">
        {/* Sports Nets Video Background */}
        <div className="absolute inset-0 w-full h-full">
          <video
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            className="w-full h-full object-cover opacity-50"
            onError={(e) => {
              console.log('Video failed to load, using fallback image');
              const fallbackDiv = document.createElement('div');
              fallbackDiv.className = 'absolute inset-0 w-full h-full bg-cover bg-center bg-no-repeat';
              fallbackDiv.style.backgroundImage = `url('https://images.unsplash.com/photo-1504307651254-35680f356dfd?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80')`;
              if (e.currentTarget.parentNode) {
                e.currentTarget.parentNode.appendChild(fallbackDiv);
                e.currentTarget.style.display = 'none';
              }
            }}
            onLoadStart={() => {
              console.log('Attempting to load sports-nets video...');
            }}
            onCanPlay={() => {
              console.log('Video loaded successfully and can play');
            }}
            onLoadedData={(e) => {
              console.log('Video data loaded');
              e.currentTarget.play().catch(err => console.log('Auto-play failed:', err));
            }}
          >
            <source src="/videos/sports-nets.mp4" type="video/mp4" />
            <source src="/videos/sports-nets.webm" type="video/webm" />
          </video>
        </div>
        
        {/* Dark Overlay for Better Text Visibility */}
        <div className="absolute inset-0 bg-black opacity-50"></div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl lg:text-5xl font-bold mb-6">
            Our Safety Net Products
          </h1>
          <p className="text-xl text-white max-w-3xl mx-auto font-medium">
            Premium quality safety nets for construction, sports, industrial, and residential applications across all areas of Hyderabad including Secunderabad, Kukatpally, Gachibowli, HITEC City, Banjara Hills, Jubilee Hills, Begumpet, Ameerpet, Somajiguda, Abids, Koti, Nampally, Malakpet, Dilsukhnagar, L.B. Nagar, Uppal, Kothapet, Hayathnagar, Vanasthalipuram, Nagole, Boduppal, Ghatkesar, Medchal, Shamshabad, Rajendranagar, Serilingampally, Qutubullapur, Malkajgiri, Alwal, Sainikpuri, Kapra, Nacharam, ECIL, Tarnaka, Habsiguda, Moula Ali, Bowenpally, Trimulgherry, Karkhana, Bolarum, BHEL, Patancheru, Jeedimetla, Balanagar, Miyapur, Bachupally, Nizampet, Kompally, Suchitra, Dundigal, Shamirpet, and all surrounding areas. 
            Professional safety net products with certified materials and comprehensive warranties.
          </p>
        </div>
      </section>

      {/* Main Services */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Safety Net Products
            </h2>
            <p className="text-xl text-gray-600">
              Choose the perfect safety net product for your protection needs - serving all areas of Hyderabad including Secunderabad, Kukatpally, Gachibowli, HITEC City, Banjara Hills, Jubilee Hills, Begumpet, Ameerpet, Somajiguda, Abids, Koti, Nampally, Malakpet, Dilsukhnagar, L.B. Nagar, Uppal, Kothapet, Hayathnagar, Vanasthalipuram, Nagole, Boduppal, Ghatkesar, Medchal, Shamshabad, Rajendranagar, Serilingampally, Qutubullapur, Malkajgiri, Alwal, Sainikpuri, Kapra, Nacharam, ECIL, Tarnaka, Habsiguda, Moula Ali, Bowenpally, Trimulgherry, Karkhana, Bolarum, BHEL, Patancheru, Jeedimetla, Balanagar, Miyapur, Bachupally, Nizampet, Kompally, Suchitra, Dundigal, Shamirpet, and all surrounding areas
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {products.map((product, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 hover:-translate-y-2"
              >
                {/* Service Header with Image */}
                <div className="h-64 bg-gray-50 relative overflow-hidden border-b border-gray-200">
                  {index === 0 && (
                    <img 
                      src="/images/invisible-grill-balconies.jpg"
                      alt="Balcony Grills"
                      className="w-full h-full object-cover"
                    />
                  )}
                  {index === 1 && (
                    <img 
                      src="/images/invisible-grill-windows.jpg"
                      alt="Window Grills"
                      className="w-full h-full object-cover"
                    />
                  )}
                  {index === 2 && (
                    <img 
                      src="/images/invisible-grill-apartments.jpg"
                      alt="Apartment Grills"
                      className="w-full h-full object-cover"
                    />
                  )}
                  {index === 3 && (
                    <img 
                      src="/images/pigeon-nets-specific.webp"
                      alt="Anti Bird Net"
                      className="w-full h-full object-cover"
                    />
                  )}
                  {index === 4 && (
                    <img 
                      src="/images/cricket-practice-nets-custom.jpg"
                      alt="Cricket Practice Net"
                      className="w-full h-full object-cover"
                    />
                  )}
                  {index === 5 && (
                    <img 
                      src="/images/children-safety-nets-custom.jpg"
                      alt="Children Safety Nets"
                      className="w-full h-full object-cover"
                    />
                  )}
                  {index === 6 && (
                    <img 
                      src="/images/pull-dry-cloth-hangers-balconies1.jpg"
                      alt="Drying Cloth Hangers"
                      className="w-full h-full object-cover"
                    />
                  )}
                  {index === 7 && (
                    <img 
                      src="/images/shade-nets-agricultural.webp"
                      alt="Shade Net"
                      className="w-full h-full object-cover"
                    />
                  )}
                  {index === 8 && (
                    <img 
                      src="/images/balcony-safety-nets-custom.jpg"
                      alt="Balcony Safety Nets"
                      className="w-full h-full object-cover"
                    />
                  )}
                  <img
                    src="/images/jaranets-logo.png"
                    alt="JaraNets Logo"
                    className="absolute top-2 left-2 h-8 w-auto drop-shadow-md pointer-events-none select-none"
                  />
                </div>
                
                <div className="p-8">
                  <div className="text-center mb-4">
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">{product.title}</h3>
                  </div>
                  <p className="text-gray-600 mb-6 text-center">
                    {product.description}
                  </p>
                  
                  <div className="mb-6">
                    <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                      <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                      Key Features:
                    </h4>
                    <ul className="space-y-2">
                      {product.features.map((feature, idx) => (
                        <li key={idx} className="flex items-center text-gray-600">
                          <div className="w-2 h-2 bg-yellow-500 rounded-full mr-3 flex-shrink-0"></div>
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="mb-6 grid grid-cols-2 gap-4 text-sm">
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <div className="font-semibold text-gray-900">Material</div>
                      <div className="text-gray-600">{product.material}</div>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <div className="font-semibold text-gray-900">Warranty</div>
                      <div className="text-gray-600">{product.warranty}</div>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-center gap-3">
                    <Link 
                      to={productRoutes[index] || '/products'}
                      className="px-5 py-3 rounded-lg font-semibold transition-colors inline-flex items-center bg-blue-600 hover:bg-blue-700 text-white"
                    >
                      Read More
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Link>
                    <button 
                      onClick={() => openWhatsAppQuote()}
                      className="bg-yellow-500 hover:bg-yellow-600 text-gray-900 px-6 py-3 rounded-lg font-semibold transition-colors inline-flex items-center group cursor-pointer"
                    >
                      Get Quote
                      <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Services */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Additional Services
            </h2>
            <p className="text-xl text-gray-600">
              Complete support throughout your solar journey
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {additionalServices.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center p-6"
              >
                <div className="mb-4 flex justify-center">{service.icon}</div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  {service.title}
                </h3>
                <p className="text-gray-600">
                  {service.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Safety Standards */}
      <section className="py-20 bg-green-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Safety Standards Benefits
            </h2>
            <p className="text-xl mb-8 max-w-3xl mx-auto">
              Avail government compliance and safety certifications for safety net installation. 
              Ensure maximum protection with certified safety standards.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="bg-green-700 p-6 rounded-lg">
                <div className="text-3xl font-bold mb-2">100%</div>
                <div>Safety Compliance</div>
              </div>
              <div className="bg-green-700 p-6 rounded-lg">
                <div className="text-3xl font-bold mb-2">ISO</div>
                <div>Certified Materials</div>
              </div>
              <div className="bg-green-700 p-6 rounded-lg">
                <div className="text-3xl font-bold mb-2">5+</div>
                <div>Years Warranty</div>
              </div>
            </div>
            <Link
              to="/landing"
              className="bg-white text-green-600 hover:bg-gray-100 px-8 py-4 rounded-lg font-semibold text-lg transition-colors inline-block"
            >
              Get Safety Standards Details
            </Link>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold mb-6">
            Ready to Get Protected?
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Contact us for a free consultation and customized safety solution quote
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:+918074616049"
              className="bg-blue-500 hover:bg-blue-600 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-colors"
            >
                              Call Now: +91 8074 616 049
            </a>
            <a
              href="tel:+918466055207"
              className="bg-green-500 hover:bg-green-600 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-colors"
            >
              Call Now: +91 8466 055 207
            </a>
            <a
              href="https://wa.me/918074616049"
              target="_blank"
              rel="noopener noreferrer"
              className="border-2 border-white hover:bg-white hover:text-gray-900 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-colors"
            >
              WhatsApp Us
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Products;
